import { LoadableElement } from '@99designs/common/components/LoadableElement';
import { claimVideoBrief, claimVideoBriefVariables } from '__generated__/claimVideoBrief';
import {
    getVideoBrief,
    getVideoBrief_structuredBrief,
    getVideoBriefVariables,
} from '__generated__/getVideoBrief';
import { BriefState } from '__generated__/types';
import {
    updateVideoBrief,
    updateVideoBrief_updateStructuredBrief_errors as VideoValidationError,
    updateVideoBrief_updateStructuredBrief_fields_SmartShootVideoFields as VideoBriefFields,
    updateVideoBriefVariables,
} from '__generated__/updateVideoBrief';
import { handleGraphQLError } from 'common/utils/Apollo';
import { AuthConsumer } from 'projects/components/AuthContext';
import ContactForm from 'projects/components/SmartShoot/ContactForm';
import ProjectDetailsForm from 'projects/components/SmartShoot/ProjectDetailsForm';
import { SmartShootBrief } from 'projects/components/SmartShoot/SmartShootBrief';
import VideoShootForm from 'projects/components/SmartShoot/VideoShootForm';
import { trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import queries from 'projects/routes/SmartShootVideoBrief.graphql';
import React from 'react';
import { ApolloConsumer, Query } from 'react-apollo';
import { redirectToSmartShootGetStarted } from '.';

class SmartShootVideoBrief extends SmartShootBrief<
    VideoBriefFields,
    getVideoBrief_structuredBrief,
    VideoValidationError
> {}

interface WrappedBriefProps {
    briefId: string;
}
const WrappedBrief: React.FunctionComponent<WrappedBriefProps> = props => (
    <ApolloConsumer>
        {query => {
            return (
                <Query<getVideoBrief, getVideoBriefVariables>
                    query={queries.getVideoBrief}
                    variables={{ id: props.briefId }}
                >
                    {({ data, error, loading }) => {
                        if (loading) {
                            return <LoadableElement isLoading={true} variations={['relative ']} />;
                        }

                        if (error) {
                            handleGraphQLError(error);
                        }

                        if (data && data.structuredBrief && data.structuredBrief.fields) {
                            if (data.structuredBrief.state === BriefState.LAUNCHED) {
                                redirectToSmartShootGetStarted(
                                    data.structuredBrief.foreignKeys[0].toString()
                                );
                            }

                            return (
                                <AuthConsumer>
                                    {({ login }) => (
                                        <SmartShootVideoBrief
                                            client={query}
                                            brief={data.structuredBrief}
                                            login={login}
                                            viewer={data.viewer}
                                            industries={data.industries}
                                            documentTitle="Tell us about your video project | 99designs"
                                            heroHeading="Tell us about your video project"
                                            heroParagraph="Complete this short creative brief so we can match you with the perfect filmmaker for the job."
                                            paymentRoute={routes.videoBriefPayment(
                                                data.structuredBrief.id
                                            )}
                                            trackViewedEvent={() =>
                                                trackViewedBriefEvent({
                                                    id: data.structuredBrief.id,
                                                    page: 'Video',
                                                })
                                            }
                                            onSave={changes => {
                                                return query
                                                    .mutate<
                                                        updateVideoBrief,
                                                        updateVideoBriefVariables
                                                    >({
                                                        mutation: queries.updateVideoBrief,
                                                        variables: {
                                                            id: data.structuredBrief.id,
                                                            fields: {
                                                                smartShootVideo: Object.assign(
                                                                    {},
                                                                    changes
                                                                ),
                                                            },
                                                        },
                                                    })
                                                    .then(res => {
                                                        return res
                                                            ? res.data!.updateStructuredBrief.errors
                                                            : [];
                                                    });
                                            }}
                                            onClaim={() => {
                                                return query.mutate<
                                                    claimVideoBrief,
                                                    claimVideoBriefVariables
                                                >({
                                                    mutation: queries.claimVideoBrief,
                                                    variables: {
                                                        id: data.structuredBrief.id,
                                                    },
                                                });
                                            }}
                                            briefType="video"
                                        >
                                            {form => {
                                                return (
                                                    <>
                                                        <ProjectDetailsForm step={1} form={form} />
                                                        <VideoShootForm step={2} form={form} />
                                                        <ContactForm
                                                            step={3}
                                                            form={form}
                                                            industries={data.industries}
                                                            briefType="video"
                                                        />
                                                    </>
                                                );
                                            }}
                                        </SmartShootVideoBrief>
                                    )}
                                </AuthConsumer>
                            );
                        }
                        return null;
                    }}
                </Query>
            );
        }}
    </ApolloConsumer>
);

export default WrappedBrief;
